/** @jsxImportSource @emotion/react */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextBubble from '@design-system/text-bubble';
import ProgressBar from '@design-system/progress-bar';
import Spinner from '@design-system/spinner';
import { ETheme } from '@datatype/type';
import { ESERVER_ERROR_CODE } from '@datatype/enum';
import { IS_IYAGIGO_MOBILE_VIEW, IS_REACT_NATIVE_APP } from '@config/environment';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import BottomContainer from '@component/common/architecture/bottom-container';
import OtterTeacher from '@component/common/otter-teacher';
import Gap from '@component/common/gap';
import { MAX_RECORDING_MS, TOO_SHORT_RECORDING, useAudioTranscription } from '@hook/iyagigo/useAudioTranscription';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import { addFurigana, createFuriganaMap, normalizeExplanation } from '@util/string';
import { useCheckAttendance, useRecordStudy } from '@hook/iyagigo/useStudent';
import { useActiveTime } from '@hook/common/useEvent';
import { EApiAttendanceType, EApiLanguageCode, EApiStudyType } from '@datatype/iyagigo/api-type';
import Spacer from '@component/common/spacer';
import {
  useSpeakingSessionQuery,
  useSpeakingTurnTranscribeMutation,
  useSpeakingTurnAnalyzeMutation,
  useSpeakingTurnRespondMutation,
  useRemoveSpeakingTurnMutation,
} from '@hook/iyagigo/useApi';

// 생활회화·기본표현과 같은 방식. 한자 위 요미가나를 본문 텍스트에 직접 넣는다.
const toBubbleFurigana = (text: string, pronunciation?: string | null) => {
  if (!pronunciation) return text;

  const furiganaMap = createFuriganaMap(text, pronunciation);
  Array.from(furiganaMap.keys()).forEach((notation) => {
    if (/^\d+$/.test(notation)) furiganaMap.delete(notation);
  });

  return addFurigana(text, furiganaMap, 'start');
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  width: 100%;
`;

// 왼쪽 아바타 + 오른쪽 상황·목표
const SceneHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const SceneAvatarColumn = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  flex-shrink: 0;
`;

const TaskInfoColumn = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  height: 100%;
`;

const TaskDescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const SpeakingCountContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
`;

const SpeakingProgressContainer = styled.div`
  flex: 1;
`;

// 아바타 하단에 걸치는 이름표
const SpeakerNameTag = styled.div`
  position: relative;
  z-index: 1;
  margin-top: -14px;
  padding: 3px ${size.MARGIN_2};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_3};
  background-color: ${color.WHITE_1};
`;

// 대화 로그. 시간순으로 쌓기만 한다 — 맨 아래가 최신이라는 규칙 하나로 읽힌다.
// 생활회화 ReadConversationContainer와 같은 모양이다.
// 다만 거기는 대본이 미리 다 있어 늘 화면을 넘기지만, AI 말하기는 한 줄부터 시작한다.
// flex: 1로 남은 높이를 채워 대화 초반에 아래가 비어 보이지 않게 한다.
const Conversation = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
  width: 100%;
  padding: ${size.MARGIN_2};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BROWN_5};
`;

const ConversationBottomCard = styled.div`
  position: absolute;
  z-index: 3;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-flow: column nowrap;
  padding: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid;
  border-radius: ${size.BORDER_RADIUS_2};
`;

const ContinuationHintCard = styled(ConversationBottomCard)`
  border-color: ${color.BLUE_2};
  background-color: ${color.BLUE_2};
`;

const MySttCard = styled(ConversationBottomCard)`
  border-color: ${color.BLUE_3};
  background-color: ${color.BLUE_5};
`;

const MySttFooter = styled.div`
  display: flex;
  align-items: center;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

// 버튼 위 상태 영역. 녹음 중에는 파형+시간, 평소에는 안내 문구가 같은 자리에 들어간다.
// 높이는 BottomContainer가 잡으므로 남는 공간을 채우기만 한다.
const RecordingStatusContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 5px;
`;

// 음량(dB)에 따라 높이가 변하는 막대. RN이 보내는 metering으로 채운다.
const WaveContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 24px;
`;

const WaveBar = styled.div<{ $height: number }>`
  width: 3px;
  height: ${({ $height }) => $height}px;
  border-radius: 2px;
  background-color: ${color.BROWN_1};
  transition: height 0.1s linear;
`;

// 하단 컨트롤 — 마이크를 중앙에 고정하고, 이어 말하기 예시가 있을 때만 오른쪽에 힌트를 둔다.
const ControlRowContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 60px minmax(0, 1fr);
  align-items: end;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

// 녹음 버튼. 배경색은 녹음 중 여부로, 흐리기는 전사 중 여부로 호출부에서 덧씌운다.
// 녹음 영역 높이. 상태(파형+시간/안내문구)와 버튼이 함께 들어간다. 여기서 한 번 잡아 두면
// 상태가 바뀌어도 대화창과 버튼이 흔들리지 않는다.
const BOTTOM_HEIGHT = '130px';
const HINT_BUTTON_WIDTH = '100px';

const RECORD_BUTTON_BOX_DESIGN = {
  width: '60px',
  height: '60px',
  alignX: 'center',
  alignY: 'center',
  borderRadius: '50%',
} as const;

const HINT_CARD_ICON_BOX_DESIGN = {
  width: '25px',
  height: '25px',
  borderRadius: '50%',
  backgroundColor: color.YELLOW_1,
  alignX: 'center',
  alignY: 'center',
} as const;

const HINT_BUTTON_BOX_DESIGN = {
  width: HINT_BUTTON_WIDTH,
  height: '35px',
  borderWidth: size.BORDER_WIDTH_1,
  borderColor: color.BLUE_2,
  borderRadius: size.BORDER_RADIUS_1,
  backgroundColor: color.WHITE_1,
  event: { cursor: 'pointer' },
} as const;

// 말풍선 + 실패 시 재시도 버튼. 사용자 턴은 오른쪽, AI 턴은 왼쪽에 맞춘다.
const TurnColumn = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  gap: 4px;
  width: 100%;
`;

const TurnControlContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

const TurnControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
  margin-top: 5px;
`;

const HiddenAudio = styled.audio`
  display: none;
`;

// 교정 설명·표현 설명. 간편이야기·생활회화의 상세 설명과 같은 회색을 쓴다.
const TurnNote = styled.div`
  width: 100%;
  margin-top: 6px;
  padding-top: 6px;
  border-top: ${size.BORDER_WIDTH_1} dashed ${color.GRAY_BOX_BORDER_COLOR};
`;

const TurnTranslationContainer = styled.div`
  width: 100%;
  margin-top: 4px;
  padding: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
`;

const ContinuationButtonSpacer = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContinuationButtonContainer = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  justify-content: flex-end;
`;

// 이전 점검 탈락 안내. 사용자 확인 후에는 점수만으로 재녹음을 요구하지 않는다.
// const REJECTED_TURN_TEXT = '조금 더 또렷하게\n다시 말해주세요.';
// const MIN_TURN_SCORE = 2;
const FAILED_BUBBLE_TEXT = '대화를 잠시 놓쳤어요.\n다시 한번 말해주세요.';
// 처리 오류 안내 후보: '주변이 시끄러워요.\n다시 한번 말해주세요.' (소음 여부를 확인할 수 없어 미사용)

// AI 응답을 기다리는 동안 표시하는 말풍선 문구.
const AI_LOADING_TEXT = '생각하고 있어요';

// 파형 막대 개수. 음량에 따라 가운데가 높게 보이도록 가중치를 준다.
const WAVE_BAR_WEIGHTS = [0.4, 0.7, 1, 0.7, 0.4];
const WAVE_BAR_MIN_HEIGHT = 4;
const WAVE_BAR_MAX_HEIGHT = 24;

// RN metering은 dB(대략 -160 ~ 0)로 들어온다. 0~1로 정규화한다.
const normalizeMetering = (metering: number) => {
  const MIN_DB = -50;
  if (!Number.isFinite(metering)) return 0;
  if (metering >= 0) return 1;
  if (metering <= MIN_DB) return 0;
  return (metering - MIN_DB) / -MIN_DB;
};

const formatDuration = (durationMs: number) => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

// 경과시간 옆에 상한을 함께 보여 준다. 따로 안내하지 않아도 언제 끊기는지 읽힌다.
const RECORDING_LIMIT_TEXT = formatDuration(MAX_RECORDING_MS);

interface ITurnControlProps {
  audioLink: string | null;
  translation: string | null;
  // 학습자 턴은 교정 설명, AI 턴은 배울 표현 설명
  note: string | null;
  isUser: boolean;
  isAnalyzing: boolean;
}

/** 말풍선 아래 재생·해석 버튼. 생활회화처럼 네이티브 플레이어는 숨기고 아이콘만 노출한다. */
const TurnControl: React.FC<ITurnControlProps> = ({ audioLink, translation, note, isUser, isAnalyzing }) => {
  const [isTranslationVisible, setIsTranslationVisible] = useState(false);
  const { isPlaying, audioRefs, handlePlayCompleted, handlePause, handlePlayAndPauseClick } = useAudioPlayer({
    sources: audioLink ? [audioLink] : [],
  });

  return (
    <TurnControlContainer $isUser={isUser}>
      <TurnControlRow>
        {audioLink && (
          <>
            <IconBlock
              icon={isPlaying ? 'volume-off' : 'volume-on'}
              design={{
                icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: handlePlayAndPauseClick }}
            />
            <HiddenAudio
              data-speaker={isUser ? 'USER' : 'AI'}
              ref={(element) => {
                if (element) audioRefs.current[0] = element;
              }}
              src={audioLink}
              onPause={handlePause}
              onEnded={handlePlayCompleted}
            />
          </>
        )}
        {isAnalyzing ? (
          <Spinner size='16px' thickness='2px' color={color.GRAY_1} />
        ) : (
          (translation || note) && (
            <IconBlock
              icon={isTranslationVisible ? 'text-snippet-off' : 'text-snippet'}
              design={{
                icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: () => setIsTranslationVisible((visible) => !visible) }}
            />
          )
        )}
      </TurnControlRow>
      {isTranslationVisible && (translation || note) && (
        <TurnTranslationContainer>
          {translation && (
            <TextBlock
              text={translation}
              design={{ text: { size: 'small', color: color.BLACK_2 }, box: { alignX: 'left' } }}
            />
          )}
          {note && (
            <TurnNote>
              <TextBlock
                text={normalizeExplanation(note, color.GRAY_1)}
                design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
              />
            </TurnNote>
          )}
        </TurnTranslationContainer>
      )}
    </TurnControlContainer>
  );
};

interface IProps {
  sessionKey: string;
  isReadOnly?: boolean;
  handleCompleteClick: () => void;
}

const SpeakingStudy: React.FC<IProps> = ({ sessionKey, isReadOnly = false, handleCompleteClick }) => {
  const { data: sessionData, isLoading: isSessionLoading } = useSpeakingSessionQuery(sessionKey);
  const transcriptionMutation = useSpeakingTurnTranscribeMutation(sessionKey);
  const analysisMutation = useSpeakingTurnAnalyzeMutation(sessionKey);
  const responseMutation = useSpeakingTurnRespondMutation(sessionKey);
  const removalMutation = useRemoveSpeakingTurnMutation(sessionKey);
  const session = sessionData?.data;
  const task = session?.speakingTask ?? null;
  const isEnglishStudy = session?.languageCode === EApiLanguageCode.ENGLISH;
  const isFreeTalk = !!session && !task;
  const taskDescription = task?.description?.ko ?? (isFreeTalk ? '원하는 주제로 이야기해요' : null);
  const partnerName = task?.context?.role?.name ?? 'AI';
  // 서버 대화는 useApi.ts가 갱신한 query 값만 읽는다.
  const turns = session?.speakingTurns ?? [];
  const latestTurn = turns[turns.length - 1];
  // 전사 후 확인 대기는 서버 원문과 미분석 점수에서 복원한다.
  const pendingTurn =
    latestTurn?.speaker === 'USER' && latestTurn.text && (latestTurn.checkScore == null || latestTurn.checkScore === 0)
      ? latestTurn
      : null;
  const confirmationActionRef = useRef(false);
  const [submittedTurnKey, setSubmittedTurnKey] = useState<string | null>(null);
  const isAwaitingConfirmation =
    !!pendingTurn && pendingTurn.key !== submittedTurnKey && !isReadOnly && !session?.completedAt;
  const isTaskCompleted = turns.some((turn) => turn.isTaskCompleted === true);
  // 하단 안내: none=정상, tooShort=최소 녹음 시간 미달, tooLong=30초 자동 종료,
  // failed=녹음·전사·분석 실패 또는 점검 탈락. 새 녹음을 시작하면 초기화한다.
  const [turnFailure, setTurnFailure] = useState<'none' | 'tooShort' | 'tooLong' | 'failed'>('none');
  const [isContinuationVisible, setIsContinuationVisible] = useState(false); // 힌트 카드 열림 여부
  // AI 오프닝·응답 자동재생용 오디오.
  const aiAudioRef = useRef<HTMLAudioElement | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPartnerSpeaking, setIsPartnerSpeaking] = useState(false);
  // 첫 오프닝과 새 응답만 자동 재생한다. 이미 진행된 지난 대화는 재생하지 않는다.
  // 같은 화면에서 연속 발화할 때 세션 개수의 중복 증가를 막는다.
  const countedSessionKeysRef = useRef(new Set<string>());
  const autoPlayTurnKeyRef = useRef<string | null>(null);
  const openingAutoPlaySessionKeyRef = useRef<string | null>(null);
  const conversationScrollRef = useRef<HTMLDivElement | null>(null);
  const {
    microphoneState,
    durationMs,
    metering,
    isFinishingRecording,
    checkMicrophonePermission,
    openSystemSetting,
    startRecording,
    finishRecording,
    cancelRecording,
  } = useAudioTranscription(sessionKey);

  // API 진행 상태는 mutation이 관리한다. 대기 말풍선은 화면에만 표시한다.
  const isTranscribing = isFinishingRecording || transcriptionMutation.isPending;
  const isTurnProcessing =
    isTranscribing || responseMutation.isPending || removalMutation.isPending;
  // 2026-10-22 이전 동작 보존: 분석 점수에 따른 응답 숨김 판정.
  // const isAnalysisRejected =
  //   analysisMutation.isError ||
  //   (analysisMutation.isSuccess &&
  //     (!analysisMutation.data?.data ||
  //       (analysisMutation.data.data.checkScore != null && analysisMutation.data.data.checkScore <= MIN_TURN_SCORE)));

  const isResponseFailed = responseMutation.isError || (responseMutation.isSuccess && !responseMutation.data?.data);
  // 분석은 교정·해설만 보완한다. 응답 표시와 다음 녹음은 분석 완료를 기다리지 않는다.
  const isAiResponseLoading = responseMutation.isPending;

  const checkAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  // 조회 완료 시각과 새 AI 응답의 완료 시각을 원본으로 사용한다. 별도 state로 복사하지 않는다.
  const isSessionCompleted = !!session?.completedAt;
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(!isSessionCompleted && !isReadOnly);

  const isRecording = microphoneState === 'recording';
  const WaveBars = WAVE_BAR_WEIGHTS.map((weight, index) => {
    const level = normalizeMetering(metering) * weight;
    const height = WAVE_BAR_MIN_HEIGHT + (WAVE_BAR_MAX_HEIGHT - WAVE_BAR_MIN_HEIGHT) * level;
    return <WaveBar key={index} $height={height} />;
  });

  // 권한 상태에 따라 안내와 버튼 문구가 달라진다.
  // blocked는 OS 팝업이 더 이상 뜨지 않으므로 설정 앱으로 보내야 한다.
  // 과제 완료·최대 턴 도달은 서버의 respond 응답으로 결정한다.
  const maxUserTurn = session?.maxUserTurn ?? null;
  const userTurnCount = turns.filter(
    // 이전 기준: turn.speaker === 'USER' && turn.checkScore != null && turn.checkScore > MIN_TURN_SCORE
    (turn) => turn.speaker === 'USER' && !!turn.text && (!isAwaitingConfirmation || turn.key !== pendingTurn?.key),
  ).length;
  // 안내 우선순위: 세션 완료 → 조회 → 전사 → AI 응답 → 녹음 실패 → 권한 → 기본 안내.
  let guideText = '마이크를 누르고 말해보세요';
  let isFailureGuide = false;
  if (isReadOnly) {
    guideText = '지난 대화 기록이에요';
  } else if (isSessionCompleted) {
    guideText =
      maxUserTurn != null && userTurnCount >= maxUserTurn
        ? `${maxUserTurn}번의 말하기를 모두 마쳤어요`
        : '말하기 목표를 모두 채웠어요';
  } else if (isSessionLoading) {
    guideText = '대화를 준비하고 있어요';
  } else if (isTranscribing) {
    guideText = '말을 듣고 있어요';
  } else if (isAiResponseLoading) {
    guideText = '답변을 생각하고 있어요';
  } else if (pendingTurn && !isTurnProcessing && turnFailure !== 'failed') {
    guideText = '다시 말하려면 마이크를 눌러주세요';
  } else if (turnFailure === 'failed') {
    const isSpeechNotDetected = transcriptionMutation.error?.response?.data?.code === ESERVER_ERROR_CODE.CLIENT_0006;
    guideText = isSpeechNotDetected
      ? '목소리가 들리지 않았어요.\n다시 말해주세요.'
      : FAILED_BUBBLE_TEXT;
    isFailureGuide = true;
  } else if (isResponseFailed) {
    guideText = FAILED_BUBBLE_TEXT;
    isFailureGuide = true;
  } else if (turnFailure !== 'none') {
    guideText = { tooShort: '너무 짧아요', tooLong: '시간이 다 됐어요' }[turnFailure];
    isFailureGuide = true;
  } else if (microphoneState === 'blocked') {
    guideText = IS_REACT_NATIVE_APP
      ? '설정 > 권한에서 마이크를 켜주세요.'
      : '주소창의 자물쇠를 눌러 마이크를 허용해 주세요';
  } else if (microphoneState === 'denied') {
    guideText = '마이크를 허용하면 말하기를 시작할 수 있어요';
  } else if (microphoneState === 'error') {
    guideText = '녹음에 문제가 생겼어요, 다시 시도해 주세요';
  } else if (isTaskCompleted) {
    guideText = '말하기 목표를 모두 채웠어요\n더 이야기해도 좋아요';
  }

  // 말풍선이 늘거나 내용이 채워지면 맨 아래로 따라간다. 지금 오간 말이 늘 보여야 한다.
  useEffect(() => {
    const container = conversationScrollRef.current;
    if (!container) return;

    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [
    session?.speakingTurns,
    isSessionLoading,
    transcriptionMutation.isPending,
    analysisMutation.isPending,
    responseMutation.isPending,
  ]);

  // 화면 진입 시 권한을 미리 확보하고, 이탈 시 녹음을 정리한다.
  // cancel을 보내지 않으면 녹음이 백그라운드에서 계속 돌고 마이크가 풀리지 않는다.
  useEffect(() => {
    if (!isReadOnly) checkMicrophonePermission();
    return () => cancelRecording();
  }, [checkMicrophonePermission, cancelRecording, isReadOnly]);

  // 오프닝만 있는 미완료 세션은 첫 마디를 한 번 자동 재생한다.
  useEffect(() => {
    if (isReadOnly || !session || session.completedAt || openingAutoPlaySessionKeyRef.current === sessionKey) return;

    const openingTurn = session.speakingTurns?.[0];
    if (session.speakingTurns?.length === 1 && openingTurn?.speaker === 'AI' && openingTurn.audioLink) {
      openingAutoPlaySessionKeyRef.current = sessionKey;
      autoPlayTurnKeyRef.current = openingTurn.key;
    }
  }, [session, sessionKey, isReadOnly]);

  // 응답 데이터는 useApi.ts가 query에 반영한다. 화면은 재생·출석만 처리한다.
  useEffect(() => {
    const responded = responseMutation.data?.data;
    if (!responded) return;
    autoPlayTurnKeyRef.current = responded.audioLink ? responded.key : null;
    if (responded.sessionCompletedAt) {
      checkAttendance(isEnglishStudy ? EApiAttendanceType.ENGLISH_SPEAKING : EApiAttendanceType.JAPANESE_SPEAKING);
    }
  }, [responseMutation.data]);

  // 세션 조회 또는 턴 처리 중에는 녹음을 막는다.
  // 완료 상태에서는 녹음 버튼이 나가기 버튼으로 바뀌므로 클릭할 수 있다.
  const isRecordControlDisabled = !isReadOnly && !isSessionCompleted && (isSessionLoading || isTurnProcessing);

  // 가장 최근 AI 턴을 자동재생과 힌트에 사용한다.
  const latestPartnerTurn = [...turns].reverse().find((turn) => turn.speaker === 'AI') ?? null;
  useEffect(() => {
    const audio = aiAudioRef.current;
    if (!audio || !latestPartnerTurn?.audioLink || autoPlayTurnKeyRef.current !== latestPartnerTurn.key) return;

    autoPlayTurnKeyRef.current = null;
    activeAudioRef.current?.pause();
    audio.currentTime = 0;
    // 차단·음원 오류를 숨기지 않는다. 말풍선 버튼으로 직접 재생할 수도 있다.
    void audio.play().catch((error) => {
      // Error/DOMException은 브리지에서 직렬화하면 {}가 되어 속성을 직접 기록한다.
      console.warn('=== speaking audio autoplay failed ===', {
        turnKey: latestPartnerTurn.key,
        errorName: error?.name,
        errorMessage: error?.message ?? String(error),
        audioErrorCode: audio.error?.code,
        audioErrorMessage: audio.error?.message,
        readyState: audio.readyState,
        networkState: audio.networkState,
        paused: audio.paused,
        currentTime: audio.currentTime,
      });
    });
    return () => audio.pause();
  }, [latestPartnerTurn?.key, latestPartnerTurn?.audioLink]);
  const continuationText = latestPartnerTurn?.continuationText ?? null;
  const continuationPronunciation = isEnglishStudy ? null : latestPartnerTurn?.continuationPronunciation;
  const continuationTranslation = latestPartnerTurn?.continuationTranslation ?? null;
  const canShowContinuation =
    !isReadOnly && !isSessionCompleted && !isSessionLoading && !isTurnProcessing && !pendingTurn && !!continuationText;

  useEffect(() => {
    setIsContinuationVisible(false);
  }, [latestPartnerTurn?.key, isSessionLoading, isTurnProcessing]);

  const ConversationTurns = turns
    .filter((turn) => {
      if (isReadOnly || turn.speaker !== 'USER') return true;
      // 미전송 원문은 입력 영역에 두고, 보내기를 누르면 대화에 표시한다.
      if (isAwaitingConfirmation && turn.key === pendingTurn?.key) return false;
      return !!turn.text;
    })
    .map((turn) => {
      const isMe = turn.speaker === 'USER';
      const isAnalyzing = isMe && analysisMutation.isPending && analysisMutation.variables === turn.key;
      const hasContent = !!turn.text;
      const hasControl = hasContent && (turn.audioLink || turn.translation || turn.note || isAnalyzing);

      // 조회된 턴의 내용이 비어 있으면 실패 안내를 표시한다.
      const bubbleText = hasContent ? toBubbleFurigana(turn.text ?? '', turn.pronunciation) : FAILED_BUBBLE_TEXT;

      return (
        <TurnColumn key={turn.key} $isUser={isMe}>
          <TextBubble
            type='plain'
            text={bubbleText}
            theme={isMe ? ETheme.BROWN : ETheme.GRAY}
            size='medium'
            align={isMe ? 'right' : 'left'}
            lineMaxWidth='calc(100% - 20px)'
            control={
              hasControl ? (
                <TurnControl
                  audioLink={turn.audioLink}
                  translation={turn.translation}
                  note={turn.note}
                  isUser={isMe}
                  isAnalyzing={isAnalyzing}
                />
              ) : undefined
            }
          />
        </TurnColumn>
      );
    });

  /** 녹음 시작 전 현재 재생 중인 음성만 멈춘다. */
  const stopActiveAudio = () => {
    const audio = activeAudioRef.current;
    activeAudioRef.current = null;
    setIsPartnerSpeaking(false);
    audio?.pause();
  };

  const handleAudioStopped = (event: React.SyntheticEvent<HTMLDivElement>) => {
    if (activeAudioRef.current !== event.target) return;
    setIsPartnerSpeaking(false);
    // 버퍼링은 다시 playing으로 이어질 수 있으므로 현재 오디오는 유지한다.
    if (event.type !== 'waiting') activeAudioRef.current = null;
  };

  /** STT 직후 원문을 표시한다. 발음·해석·교정은 나중에 같은 말풍선에 추가한다. */
  const runTranscription = async (turnKey: string) => {
    try {
      await transcriptionMutation.mutateAsync(turnKey);
    } catch {
      setTurnFailure('failed');
      await removalMutation.mutateAsync(turnKey).catch(() => undefined);
      return;
    }
  };

  // 2026-10-22 이전 동작 보존: 분석 실패·저점수 턴 삭제 및 분석 완료 후 학습 기록 처리.
  // /** 병렬 호출 후 서버 응답으로 학습 기록·실패 안내만 처리한다. */
  // const runAnalysisAndResponse = async (turnKey: string) => {
  //   const analysis = analysisMutation.mutateAsync(turnKey);
  //   // respond는 서버에서 분석 통과 여부를 확인한 뒤 AI 턴을 저장한다.
  //   const response = responseMutation.mutateAsync(turnKey);
  //
  //   // 분석이 끝나면 응답 완료를 기다리지 않고 학습 기록 또는 실패 안내를 반영한다.
  //   const analysisHandled = analysis.then(
  //     async ({ data }) => {
  //       if (!data || (data.checkScore != null && data.checkScore <= MIN_TURN_SCORE)) {
  //         setTurnFailure('failed');
  //         await removalMutation.mutateAsync(turnKey).catch(() => undefined);
  //         return;
  //       }
  //       // 재진입한 세션은 서버에 저장된 이전 유효 발화로 집계 여부를 판단한다.
  //       // 현재 턴은 mutation이 query에 반영했을 수 있으므로 비교에서 제외한다.
  //       const hasPreviousValidTurn = turns.some(
  //         (turn) =>
  //           turn.key !== turnKey &&
  //           turn.speaker === 'USER' &&
  //           turn.checkScore != null &&
  //           turn.checkScore > MIN_TURN_SCORE,
  //       );
  //       const isFirstSpeaking = !hasPreviousValidTurn && !countedSessionKeysRef.current.has(sessionKey);
  //       recordStudy({
  //         studentKey,
  //         studyType: EApiStudyType.SPEAKING,
  //         languageCode: isEnglishStudy ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  //         studyTime: getActiveTime(),
  //         ...(isFirstSpeaking ? { studyCount: 1 } : {}),
  //       });
  //       countedSessionKeysRef.current.add(sessionKey);
  //       resetActiveTime();
  //     },
  //     async () => {
  //       // 삭제 성공 후 query에서도 제거한다. 실패 원문을 로컬에 보관하지 않는다.
  //       setTurnFailure('failed');
  //       await removalMutation.mutateAsync(turnKey).catch(() => undefined);
  //     },
  //   );
  //   // 분석에서 탈락해도 이미 시작한 응답 호출이 끝날 때까지 녹음 잠금을 유지한다.
  //   await Promise.allSettled([analysisHandled, response]);
  // };

  /** 보내기에서 분석·응답을 함께 시작하되 응답 완료만 기다린다. */
  const runAnalysisAndResponse = async (turnKey: string) => {
    // 분석 실패·낮은 점수로 사용자가 확인한 발화를 삭제하지 않는다.
    void analysisMutation.mutateAsync(turnKey).catch(() => undefined);
    const response = responseMutation.mutateAsync(turnKey);

    // 확인해 보낸 발화를 학습으로 기록한다. 전사 미리보기만으로는 기록하지 않는다.
    const hasPreviousSentTurn = turns.some(
      (turn) => turn.key !== turnKey && turn.speaker === 'USER' && !!turn.text,
    );
    const isFirstSpeaking = !hasPreviousSentTurn && !countedSessionKeysRef.current.has(sessionKey);
    recordStudy({
      studentKey,
      studyType: EApiStudyType.SPEAKING,
      languageCode: isEnglishStudy ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
      studyTime: getActiveTime(),
      ...(isFirstSpeaking ? { studyCount: 1 } : {}),
    });
    countedSessionKeysRef.current.add(sessionKey);
    resetActiveTime();
    // 응답 실패 안내는 responseMutation 상태로 표시한다. 분석은 계속 실행될 수 있다.
    await response.catch(() => undefined);
  };

  /** 새 녹음·재녹음 전에 이전 처리 결과를 초기화한다. */
  const resetTurnProcessing = () => {
    setTurnFailure('none');
    transcriptionMutation.reset();
    analysisMutation.reset();
    responseMutation.reset();
  };

  const handleSendClick = async () => {
    if (!pendingTurn || isReadOnly || isSessionCompleted || isTurnProcessing || confirmationActionRef.current) return;
    confirmationActionRef.current = true;
    setTurnFailure('none');
    setSubmittedTurnKey(pendingTurn.key);
    stopActiveAudio();
    try {
      await runAnalysisAndResponse(pendingTurn.key);
    } finally {
      setSubmittedTurnKey(null);
      confirmationActionRef.current = false;
    }
  };

  const handleRecordAgainClick = async () => {
    if (!pendingTurn || isReadOnly || isSessionCompleted || isTurnProcessing || confirmationActionRef.current) return;
    confirmationActionRef.current = true;
    stopActiveAudio();
    try {
      // 삭제가 성공해야 새 녹음을 시작한다. 실패하면 확인 중인 원문을 유지한다.
      await removalMutation.mutateAsync(pendingTurn.key);
      resetTurnProcessing();
      await startRecording();
    } catch {
      setTurnFailure('failed');
    } finally {
      confirmationActionRef.current = false;
    }
  };

  // 녹음 종료 후 전사까지만 진행한다. 분석·응답은 보내기를 눌러 시작한다.
  const handleRecordClick = async () => {
    if (isReadOnly || isSessionLoading || isSessionCompleted || isTurnProcessing || pendingTurn || confirmationActionRef.current) return;
    if (microphoneState === 'blocked' && IS_REACT_NATIVE_APP) return openSystemSetting();
    if (isRecording) {
      // 같은 렌더 안에서 종료를 연속 눌러도 업로드·전사는 한 번만 시작한다.
      confirmationActionRef.current = true;
      try {
        const created = await finishRecording();
        if (created === TOO_SHORT_RECORDING) {
          setTurnFailure('tooShort');
          return;
        }
        if (!created) {
          setTurnFailure('failed');
          return;
        }
        await runTranscription(created.key);
      } finally {
        confirmationActionRef.current = false;
      }
      return;
    }

    resetTurnProcessing();
    stopActiveAudio();
    await startRecording();
  };

  // 너무 길어지면 버튼을 못 뗀 상황으로 보고 녹음을 끝낸다. 말한 내용은 버리지 않고 전사 확인 단계로 보낸다.
  useEffect(() => {
    if (!isRecording || durationMs < MAX_RECORDING_MS) return;
    void (async () => {
      await handleRecordClick();
      // 전사문은 확인을 기다린다. 왜 멈췄는지만 알려 준다.
      setTurnFailure((previous) => (previous === 'none' ? 'tooLong' : previous));
    })();
  }, [isRecording, durationMs]);

  return (
    <Wrapper
      onPlayCapture={(event) => {
        const audio = event.target;
        if (!(audio instanceof HTMLAudioElement)) return;
        const previousAudio = activeAudioRef.current;
        activeAudioRef.current = audio;
        if (previousAudio !== audio) previousAudio?.pause();
      }}
      onPlayingCapture={(event) => {
        const audio = event.target;
        if (audio instanceof HTMLAudioElement && activeAudioRef.current === audio) {
          setIsPartnerSpeaking(audio.dataset.speaker === 'AI');
        }
      }}
      onWaitingCapture={handleAudioStopped}
      onErrorCapture={handleAudioStopped}
      onPauseCapture={handleAudioStopped}
      onEndedCapture={handleAudioStopped}>
      {/* 2026-10-13: Android WebView에서 crossOrigin='anonymous' 사용 시 자동재생 실패.
          로그: NotSupportedError / Failed to load because no supported source was found.
          audioErrorCode=4, MEDIA_ELEMENT_ERROR: Format error, readyState=0, networkState=3.
          같은 음원의 말풍선 수동 재생은 정상이며, crossOrigin 제거 후 자동재생도 정상 확인.
          현재는 Web Audio 음량 분석·립싱크 없이 일반 재생만 하므로 CORS 요청을 강제하지 않는다. */}
      <HiddenAudio ref={aiAudioRef} data-speaker='AI' src={latestPartnerTurn?.audioLink ?? undefined} />
      <SceneHeader>
        <SceneAvatarColumn>
          <OtterTeacher shape='circle' diameter='100px' isPlaying={isPartnerSpeaking} />
          <SpeakerNameTag>
            <TextBlock text={partnerName} design={{ text: { size: 'small', color: color.BLACK_2 } }} />
          </SpeakerNameTag>
        </SceneAvatarColumn>
        <TaskInfoColumn>
          <TaskDescriptionContainer>
            <TextBlock
              text={task?.taskName?.ko ?? (isFreeTalk ? '자유롭게 말하기' : '')}
              design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            {taskDescription && (
              <TextBlock
                text={taskDescription}
                design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
              />
            )}
          </TaskDescriptionContainer>
          <Spacer />
          {maxUserTurn != null && (
            <SpeakingCountContainer>
              <TextBlock text='말하기 횟수' design={{ text: { size: 'small', color: color.GRAY_1 } }} />
              <Gap width={size.MARGIN_1} />
              <SpeakingProgressContainer>
                <ProgressBar
                  ratio={userTurnCount / maxUserTurn}
                  width='100%'
                  height='16px'
                  text={`${userTurnCount} / ${maxUserTurn}`}
                  theme={ETheme.YELLOW}
                />
              </SpeakingProgressContainer>
            </SpeakingCountContainer>
          )}
        </TaskInfoColumn>
      </SceneHeader>
      <Gap height={size.MARGIN_1} />
      <ScrollContainer style={{ position: 'relative' }}>
        <Scroll direction='y' height='100%' containerRef={conversationScrollRef}>
          <ContentContainer>
            <Conversation>
              {ConversationTurns}
              {(isSessionLoading || isAiResponseLoading || isResponseFailed) && (
                <TurnColumn $isUser={false}>
                  <TextBubble
                    type={isSessionLoading || isAiResponseLoading ? 'loading' : 'plain'}
                    text={FAILED_BUBBLE_TEXT}
                    loadingText={AI_LOADING_TEXT}
                    theme={ETheme.GRAY}
                    size='medium'
                    align='left'
                    lineMaxWidth='calc(100% - 20px)'
                  />
                </TurnColumn>
              )}
            </Conversation>
          </ContentContainer>
        </Scroll>
        {isAwaitingConfirmation && (
          <MySttCard>
            <IconTextBlock
              icon='message-circle-text'
              text='이렇게 들었어요'
              arrangement='icon-text'
              design={{
                text: { size: 'small', color: color.BLUE_2, bold: true },
                icon: { size: 'tiny', color: color.BLUE_2, kind: 'outlined' },
                box: { alignX: 'left', gap: '2px' },
              }}
            />
            <Gap height='10px' />
            <TextBlock
              text={pendingTurn?.text ?? ''}
              design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            <Gap height='10px' />
            <MySttFooter>
              <IconTextBlock
                icon='send'
                text='보내기'
                arrangement='icon-text'
                click={{ handler: handleSendClick }}
                design={{
                  text: { size: 'medium', color: color.WHITE_1 },
                  icon: { size: 'small', color: color.WHITE_1, kind: 'outlined' },
                  box: {
                    width: '100%',
                    padding: `5px 0`,
                    // padding: `5px ${size.MARGIN_1}`,
                    backgroundColor: color.BLUE_2,
                    borderRadius: size.BORDER_RADIUS_1,
                    event: { cursor: 'pointer' },
                    opacity: isTurnProcessing ? 0.4 : 1,
                    gap: '5px',
                  },
                }}
              />
            </MySttFooter>
          </MySttCard>
        )}
        {isContinuationVisible && !isAwaitingConfirmation && continuationText && (
          <ContinuationHintCard>
            <IconBlock
              icon='lightbulb'
              design={{
                icon: { size: 'tiny', color: color.WHITE_1, kind: 'outlined' },
                box: HINT_CARD_ICON_BOX_DESIGN,
              }}
            />
            <Gap height='5px' />
            <TextBlock
              text={toBubbleFurigana(continuationText, continuationPronunciation)}
              design={{ text: { size: 'medium', color: color.WHITE_1 }, box: { alignX: 'left' } }}
            />
            {continuationTranslation && (
              <TextBlock
                text={continuationTranslation}
                design={{ text: { size: 'small', color: color.WHITE_1 }, box: { alignX: 'left' } }}
              />
            )}
          </ContinuationHintCard>
        )}
      </ScrollContainer>
      <Gap height={size.MARGIN_1} />
      <BottomContainer
        position='relative'
        height={BOTTOM_HEIGHT}
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}>
        <BottomContentContainer>
          {/* 상태는 늘 버튼 위 한 자리에 모은다. 버튼은 엄지가 닿는 맨 아래에 고정한다. */}
          <RecordingStatusContainer>
            {isRecording ? (
              <>
                <WaveContainer>{WaveBars}</WaveContainer>
                <TextBlock
                  text={`${formatDuration(durationMs)} / ${RECORDING_LIMIT_TEXT}`}
                  design={{ text: { size: 'small', color: color.BROWN_1, bold: true } }}
                />
              </>
            ) : (
              <TextBlock
                text={guideText}
                design={{
                  text: isFailureGuide
                    ? { size: 'medium', color: color.RED_2 }
                    : { size: 'medium', color: color.BROWN_1 },
                  box: {
                    alignX: 'center',
                    padding: `5px ${size.MARGIN_1}`,
                    borderWidth: size.BORDER_WIDTH_1,
                    borderColor: isFailureGuide ? color.RED_2 : color.BROWN_1,
                    borderRadius: size.BORDER_RADIUS_2,
                  },
                }}
              />
            )}
          </RecordingStatusContainer>
          <ControlRowContainer>
            <ContinuationButtonSpacer />
            <IconBlock
              icon={isReadOnly || isSessionCompleted ? 'square-arrow-right-exit' : isRecording ? 'stop' : 'mic'}
              design={{
                icon: { size: 'large', color: color.WHITE_1, kind: isRecording ? 'filled' : 'outlined' },
                box: {
                  ...RECORD_BUTTON_BOX_DESIGN,
                  backgroundColor: isReadOnly || isSessionCompleted || isRecording ? color.RED_2 : color.BROWN_1,
                  opacity: isRecordControlDisabled ? 0.4 : 1,
                  event: { cursor: isRecordControlDisabled ? 'auto' : 'pointer' },
                },
              }}
              click={{
                handler:
                  isReadOnly || isSessionCompleted
                    ? handleCompleteClick
                    : isRecordControlDisabled
                      ? () => undefined
                      : isAwaitingConfirmation
                        ? handleRecordAgainClick
                        : handleRecordClick,
              }}
            />
            <ContinuationButtonContainer>
              {canShowContinuation && (
                <IconTextBlock
                  icon='lightbulb'
                  text={isContinuationVisible ? '힌트 닫기' : '힌트 보기'}
                  arrangement='icon-text'
                  click={{ handler: () => setIsContinuationVisible((visible) => !visible) }}
                  design={{
                    text: { size: 'medium', color: color.BLUE_2 },
                    icon: { size: 'medium', color: color.BLUE_2, kind: 'outlined' },
                    box: HINT_BUTTON_BOX_DESIGN,
                  }}
                />
              )}
            </ContinuationButtonContainer>
          </ControlRowContainer>
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default SpeakingStudy;
